/**
 * ActivateAppJobModal Component
 *
 * Modal for confirming app job activation.
 * Activating an app job starts it running on its scheduled cron expression.
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import type { AppJobSanitized } from '../../../types/app-job.types';
import { useResumeAppJob } from '../hooks/useToggleAppJob';
import { getErrorMessage } from '../../../lib/utils/errors';
import { describeCronExpression } from '../../../lib/validators/app-job';

interface ActivateAppJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  appJob: AppJobSanitized;
  onSuccess?: () => void;
}

/**
 * ActivateAppJobModal component
 */
export const ActivateAppJobModal: React.FC<ActivateAppJobModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  appJob,
  onSuccess,
}) => {
  const resumeMutation = useResumeAppJob(tenantId, {
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  const handleClose = () => {
    if (!resumeMutation.isPending) {
      resumeMutation.reset();
      onClose();
    }
  };

  const handleActivate = () => {
    resumeMutation.mutate(appJob.appJobId);
  };

  const isLoading = resumeMutation.isPending;
  const error = resumeMutation.error;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Activate App Job</Modal.Header>

      <Modal.Body>
        <div className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="error">
              {getErrorMessage(error)}
            </Alert>
          )}

          {/* Warning */}
          <Alert variant="warning">
            <div className="space-y-2">
              <p className="font-semibold">This will start the scheduled job!</p>
              <p className="text-sm">
                Once activated, this job will run automatically according to its cron schedule.
                Make sure the function and payload are configured correctly.
              </p>
            </div>
          </Alert>

          {/* App Job Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">Job Details</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Name:</span>
                <span className="text-sm font-medium text-gray-900">{appJob.appJobName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Application:</span>
                <span className="text-sm font-medium text-gray-900">{appJob.applicationCode}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Function:</span>
                <code className="text-sm font-mono bg-white px-2 py-0.5 rounded border">
                  {appJob.functionName}
                </code>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Schedule:</span>
                <span className="text-sm text-gray-900">
                  {describeCronExpression(appJob.cronExpression)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Timezone:</span>
                <span className="text-sm text-gray-900">{appJob.timezone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Service Account:</span>
                <span className="text-sm text-gray-900">{appJob.serviceAccountName || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Payload preview if exists */}
          {appJob.payload && Object.keys(appJob.payload).length > 0 && (
            <div>
              <span className="text-sm font-medium text-gray-700">Payload:</span>
              <pre className="mt-2 text-xs bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto max-h-32">
                {JSON.stringify(appJob.payload, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleActivate}
            disabled={isLoading}
            loading={isLoading}
          >
            Activate Job
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
